import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { useTenantQueryActions } from '@/api/hooks/tenantQueryActions';
import { useLocalization } from '@/context/localization';

interface AiSettingsContextType {
  isAiEnabled: boolean;
  setAiEnabled: (enabled: boolean) => void;
  aiInstructions: string;
  setAiInstructions: (instructions: string) => void;
  isLoading: boolean;
}

// Create a default context value to avoid undefined during HMR
const defaultContextValue: AiSettingsContextType = {
  isAiEnabled: false,
  setAiEnabled: () => {},
  aiInstructions: '',
  setAiInstructions: () => {},
  isLoading: false,
};

const AiSettingsContext = createContext<AiSettingsContextType>(defaultContextValue);

interface AiSettingsProviderProps {
  children: ReactNode;
}

export const AiSettingsProvider = ({ children }: AiSettingsProviderProps) => {
  const [isAiEnabled, setIsAiEnabled] = useState<boolean>(false);
  const [aiInstructions, setAiInstructions] = useState<string>('');

  const { useGetTenantAiStatus } = useTenantQueryActions();

  // EWM-631: Drive `isAiEnabled` from the lightweight ai-status endpoint so non-admin
  // (Estimator) users — who can't call the admin-gated ai-config — still get the correct
  // Sidebar visibility for AI Advisory.
  // EWM-838: the same endpoint now also carries the display instruction text (EN/DE), so
  // non-admin users see the instructions on the AI Advisory page. Previously the instructions
  // came from the admin-gated ai-config endpoint and were therefore empty for Estimators.
  const { data: aiStatus, isLoading } = useGetTenantAiStatus();

  const {
    state: { locale },
  } = useLocalization();

  useEffect(() => {
    if (aiStatus) {
      setIsAiEnabled(aiStatus.aiEnabled ?? false);
    }
  }, [aiStatus]);

  // Pick the instruction text for the user's locale (falls back to empty when not set).
  useEffect(() => {
    if (aiStatus) {
      let instructions = '';
      if (locale?.startsWith('en')) {
        instructions = aiStatus.instructionsEn || '';
      } else if (locale?.startsWith('de')) {
        instructions = aiStatus.instructionsDe || '';
      }
      setAiInstructions(instructions);
    }
  }, [aiStatus, locale]);

  const setAiEnabled = (enabled: boolean) => {
    setIsAiEnabled(enabled);
  };

  return (
    <AiSettingsContext.Provider
      value={{
        isAiEnabled,
        setAiEnabled,
        aiInstructions,
        setAiInstructions,
        isLoading,
      }}
    >
      {children}
    </AiSettingsContext.Provider>
  );
};

export const useAiSettings = () => {
  return useContext(AiSettingsContext);
};
